import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  Save,
  ArrowLeft,
  Eye,
  FileText,
  Settings,
  Globe,
  Plus,
  Trash2,
  Check,
  AlertCircle,
} from 'lucide-react';
import '../styles/page-editor.css';

const PageEditorPage = () => {
  const { pageSlug } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isNew = pageSlug === 'new';

  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [pageData, setPageData] = useState({
    page_slug: '',
    page_name: '',
    is_enabled: true,
    is_published: false,
    content: {
      hero: {
        badge: '',
        title: '',
        title_highlight: '',
        subtitle: '',
        cta_primary: '',
        cta_secondary: ''
      },
      features: [],
      sections: []
    },
    seo: {
      title: '',
      description: '',
      keywords: '',
      og_title: '',
      og_description: '',
      canonical_url: ''
    }
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isNew) {
      fetchPage();
    }
  }, [pageSlug]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/content/pages/${pageSlug}?published_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      } else {
        setError('Page not found');
      }
    } catch (err) {
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish = false) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const url = isNew 
        ? `${API_URL}/api/content/pages`
        : `${API_URL}/api/content/pages/${pageSlug}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const body = isNew ? pageData : {
        page_name: pageData.page_name,
        content: pageData.content,
        seo: pageData.seo,
        is_enabled: pageData.is_enabled,
        is_published: publish ? true : pageData.is_published
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setSuccess(publish ? 'Page saved and published!' : 'Page saved as draft!');
        if (isNew) {
          const created = await response.json();
          navigate(`/admin/pages/${created.page_slug}`);
        } else {
          fetchPage();
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to save page');
      }
    } catch (err) {
      setError('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (path, value) => {
    setPageData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let obj = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addFeature = () => {
    const features = pageData.content.features || [];
    updateContent('content.features', [...features, { title: '', description: '', icon: '' }]);
  };

  const removeFeature = (index) => {
    const features = [...(pageData.content.features || [])];
    features.splice(index, 1);
    updateContent('content.features', features);
  };

  const updateFeature = (index, field, value) => {
    const features = [...(pageData.content.features || [])];
    features[index] = { ...features[index], [field]: value };
    updateContent('content.features', features);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="editor-loading">
          <div className="loading-spinner" />
          <p>Loading page...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-editor">
        {/* Header */}
        <div className="editor-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/admin/pages')}>
              <ArrowLeft size={18} />
            </button>
            <div className="header-info">
              <h2>{isNew ? 'Create New Page' : `Edit: ${pageData.page_name}`}</h2>
              {!isNew && (
                <span className={`status-badge ${pageData.is_published ? 'published' : 'draft'}`}>
                  {pageData.is_published ? 'Published' : 'Draft'}
                </span>
              )}
            </div>
          </div>
          <div className="header-actions">
            {!isNew && (
              <a 
                href={`/${pageData.page_slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <Eye size={16} /> Preview
              </a>
            )}
            <button 
              className="btn-secondary" 
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              <Save size={16} /> Save Draft
            </button>
            <button 
              className="btn-primary" 
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              <Check size={16} /> {saving ? 'Saving...' : 'Save & Publish'}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="message error">
            <AlertCircle size={18} /> {error}
          </div>
        )}
        {success && (
          <div className="message success">
            <Check size={18} /> {success}
          </div>
        )}

        {/* Tabs */}
        <div className="editor-tabs">
          <button 
            className={activeTab === 'content' ? 'active' : ''}
            onClick={() => setActiveTab('content')}
          >
            <FileText size={16} /> Content
          </button>
          <button 
            className={activeTab === 'seo' ? 'active' : ''}
            onClick={() => setActiveTab('seo')}
          >
            <Globe size={16} /> SEO
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={16} /> Settings
          </button>
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="editor-content">
            {/* Basic Info */}
            {isNew && (
              <div className="editor-section">
                <h3>Page Info</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Page Name</label>
                    <input
                      type="text"
                      value={pageData.page_name}
                      onChange={(e) => updateContent('page_name', e.target.value)}
                      placeholder="My New Page"
                    />
                  </div>
                  <div className="form-group">
                    <label>Page Slug (URL)</label>
                    <input
                      type="text"
                      value={pageData.page_slug}
                      onChange={(e) => updateContent('page_slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                      placeholder="my-new-page"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Hero Section */}
            <div className="editor-section">
              <h3>Hero Section</h3>
              <div className="form-group">
                <label>Badge Text</label>
                <input
                  type="text"
                  value={pageData.content?.hero?.badge || ''}
                  onChange={(e) => updateContent('content.hero.badge', e.target.value)}
                  placeholder="Featured Badge"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={pageData.content?.hero?.title || ''}
                    onChange={(e) => updateContent('content.hero.title', e.target.value)}
                    placeholder="Main Title"
                  />
                </div>
                <div className="form-group">
                  <label>Highlighted Text</label>
                  <input
                    type="text"
                    value={pageData.content?.hero?.title_highlight || ''}
                    onChange={(e) => updateContent('content.hero.title_highlight', e.target.value)}
                    placeholder="Highlighted"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Subtitle</label>
                <textarea
                  value={pageData.content?.hero?.subtitle || ''}
                  onChange={(e) => updateContent('content.hero.subtitle', e.target.value)}
                  placeholder="Page subtitle or description"
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Primary CTA Text</label>
                  <input
                    type="text"
                    value={pageData.content?.hero?.cta_primary || ''}
                    onChange={(e) => updateContent('content.hero.cta_primary', e.target.value)}
                    placeholder="Get Started"
                  />
                </div>
                <div className="form-group">
                  <label>Secondary CTA Text</label>
                  <input
                    type="text"
                    value={pageData.content?.hero?.cta_secondary || ''}
                    onChange={(e) => updateContent('content.hero.cta_secondary', e.target.value)}
                    placeholder="Learn More"
                  />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="editor-section">
              <div className="section-header">
                <h3>Features</h3>
                <button className="btn-add" onClick={addFeature}>
                  <Plus size={16} /> Add Feature
                </button>
              </div>
              <div className="features-list">
                {(pageData.content?.features || []).map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          placeholder="Feature title"
                        />
                      </div>
                      <div className="form-group">
                        <label>Icon</label>
                        <input
                          type="text"
                          value={feature.icon}
                          onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                          placeholder="icon-name"
                        />
                      </div>
                      <button 
                        className="btn-remove" 
                        onClick={() => removeFeature(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        placeholder="Feature description"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && (
          <div className="editor-content">
            <div className="editor-section">
              <h3>SEO Settings</h3>
              <div className="form-group">
                <label>Meta Title</label>
                <input
                  type="text"
                  value={pageData.seo?.title || ''}
                  onChange={(e) => updateContent('seo.title', e.target.value)}
                  placeholder="Page Title | VoxIT"
                />
                <span className="char-count">{(pageData.seo?.title || '').length}/60</span>
              </div>
              <div className="form-group">
                <label>Meta Description</label>
                <textarea
                  value={pageData.seo?.description || ''}
                  onChange={(e) => updateContent('seo.description', e.target.value)}
                  placeholder="Brief description of the page content"
                  rows={3}
                />
                <span className="char-count">{(pageData.seo?.description || '').length}/160</span>
              </div>
              <div className="form-group">
                <label>Keywords</label>
                <input
                  type="text"
                  value={pageData.seo?.keywords || ''}
                  onChange={(e) => updateContent('seo.keywords', e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
              <div className="form-group">
                <label>Canonical URL</label>
                <input
                  type="text"
                  value={pageData.seo?.canonical_url || ''}
                  onChange={(e) => updateContent('seo.canonical_url', e.target.value)}
                  placeholder="https://voxitconsulting.media/page"
                />
              </div>
              
              <h3 style={{ marginTop: '2rem' }}>Open Graph (Social Media)</h3>
              <div className="form-group">
                <label>OG Title</label>
                <input
                  type="text"
                  value={pageData.seo?.og_title || ''}
                  onChange={(e) => updateContent('seo.og_title', e.target.value)}
                  placeholder="Title for social media shares"
                />
              </div>
              <div className="form-group">
                <label>OG Description</label>
                <textarea
                  value={pageData.seo?.og_description || ''}
                  onChange={(e) => updateContent('seo.og_description', e.target.value)}
                  placeholder="Description for social media shares"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="editor-content">
            <div className="editor-section">
              <h3>Page Settings</h3>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageData.is_enabled}
                    onChange={(e) => updateContent('is_enabled', e.target.checked)}
                  />
                  <span>Page Enabled</span>
                </label>
                <p className="help-text">When disabled, this page will return a 404 error.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PageEditorPage;
